/**
 * A ceiling on how many of something the browser may have in flight at once.
 *
 * Written for the thumbnails, and it is worth saying what went wrong without it. The
 * Partner Units page draws a hundred cards, each with a face on it, and each face is one
 * `GET /api/media/:id/file`. React inserts all hundred `<img>` elements in the same
 * commit, so the browser opened all hundred requests inside the same fifty milliseconds
 * — over HTTP/2 there is no six-per-host queue to slow that down. Every one of them was
 * answered, and every one was inside the archive's own ceiling of 600 images a minute.
 * What refused the *next* request was not the archive: a burst like that is what a
 * scrape looks like from the edge, and Cloudflare answered the following navigation with
 * a bodyless 429 — a red "Request failed (429)" on a page that had asked for one record.
 *
 * So the fix belongs here rather than in the Worker. The page still shows a hundred
 * cards and still ends up fetching a hundred pictures; it just stops asking for them all
 * in one breath.
 *
 * Deliberately not a fetch wrapper. What is being counted is `<img>` loads, which the
 * browser performs itself and which this module never sees — so a caller is handed a
 * slot and is trusted to say when it is finished with it. `release` is idempotent and
 * must be called on failure and on unmount as well as on success, or the gate closes for
 * good on a slot nobody is using.
 *
 * No timeout, on purpose. A slot leaked by a caller that never releases is a bug in that
 * caller, and a gate that quietly reclaimed slots would hide it while also letting the
 * burst it exists to prevent back through.
 */

/**
 * @param {number} max How many jobs may run at once. Below 1 is treated as 1 — a gate
 *   that lets nothing through is not a limit, it is a hang.
 */
export function createLoadGate(max) {
  const ceiling = Math.max(1, Math.trunc(Number(max)) || 1)
  let running = 0
  const waiting = []

  const pump = () => {
    while (running < ceiling && waiting.length) {
      const job = waiting.shift()
      job.state = 'running'
      running += 1
      // Called synchronously when a slot is already free, which is the common case and
      // the one that matters: the pictures on screen when the page opens should start
      // loading in that same frame, not one tick later.
      job.start()
    }
  }

  return {
    /**
     * Queue a job. `start` runs when a slot frees up, possibly at once.
     *
     * @returns {() => void} Call it when the job has finished, failed, or been
     *   abandoned. Safe to call more than once; only the first call counts.
     */
    request(start) {
      const job = { start, state: 'waiting' }
      waiting.push(job)
      pump()
      return () => {
        if (job.state === 'done') return
        if (job.state === 'running') running -= 1
        else waiting.splice(waiting.indexOf(job), 1)
        job.state = 'done'
        pump()
      }
    },

    /** For tests, and for anyone wondering in the console why a picture is waiting. */
    stats: () => ({ running, waiting: waiting.length, ceiling }),
  }
}
